import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/api/ApiContext';
import { getImageDisplayUrl } from '@/api/handlers/resources/images';
import type { BranchListQuery } from '@/api/handlers/resources/branches/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { BranchDetailsItem } from '@/api/models';
import {
    useBranchDetailsQuery,
    useDeleteBranchMutation,
    useSetBranchArchivedMutation,
    useUpdateBranchMutation,
} from '@/api/queries';
import { useDeleteConfirm } from '@/components/deleteConfirmDialog';
import { PATH } from '@/router/PATH';
import { getUser } from '@/utils/authSession';
import { canImportProducts } from './branchImportAccess';
import { useCreateBranch } from './useCreateBranch';
import { useArchiveBranchConfirm } from './components/archiveConfirmModal';
import { BRANCHES_PAGE_SIZE, type Branch } from './components/branchesMeta';
import type {
    BranchFormInitialValues,
    BranchFormSubmit,
} from './components/branchFormModal/useBranchForm';

/** Debounce window for the search box before a new request fires. */
const SEARCH_DEBOUNCE_MS = 300;

export type UseBranchListPageResult = {
    data: Branch[] | null;
    /** Drives the delete confirmation, including a refusal and its archive path. */
    deleteConfirm: ReturnType<typeof useDeleteConfirm>;
    /**
     * Drives the archive confirmation. Archiving stops the branch trading and
     * revokes its terminal token, so it asks first; unarchiving is unguarded
     * and still goes straight through `onSetArchived`.
     */
    archiveConfirm: ReturnType<typeof useArchiveBranchConfirm>;
    /**
     * Whether the signed-in user may open Import products for a branch: the
     * `canImportProducts` rule bound to this session.
     */
    canImportProducts: (branch: Branch) => boolean;
    includeArchived: boolean;
    onRequestArchive: (id: string) => void;
    onSetArchived: (id: string, archived: boolean) => void;
    onToggleArchived: (next: boolean) => void;
    editingInitialValues: BranchFormInitialValues | null;
    error: ErrorResponse | null;
    formError: string | null;
    formMode: 'create' | 'edit';
    isFormOpen: boolean;
    isLoading: boolean;
    isSubmitting: boolean;
    onAddBranch: () => void;
    onCloseForm: () => void;
    onDeleteBranch: (id: string) => void;
    onEditBranch: (id: string) => void;
    /** Opens the branch's catalog import page. */
    onImportProducts: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onRetry: () => void;
    onSearchChange: (value: string) => void;
    onSubmitBranch: (input: BranchFormSubmit) => void;
    page: number;
    pageSize: number;
    search: string;
    totalCount: number;
    totalPages: number;
};

/**
 * Drives the Branches page. The list is **server-paginated** (`/branches/details`
 * takes page / pageSize / search and returns the matching page plus
 * totalCount / totalPages), so search and paging are pushed to the API rather
 * than done client-side — the whole branch set is never loaded at once.
 *
 * Create is one multipart POST; edit goes through `POST /images?purpose=branches`
 * first when the user picked a fresh image, then PUT with the resolved URL. All
 * writes invalidate the cached pages.
 */
export const useBranchListPage = (): UseBranchListPageResult => {
    const { apiClient, baseUrl } = useApi();

    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(BRANCHES_PAGE_SIZE);
    const [search, setSearch] = React.useState('');
    const [debouncedSearch, setDebouncedSearch] = React.useState('');
    const [includeArchived, setIncludeArchived] = React.useState(false);

    const [isFormOpen, setIsFormOpen] = React.useState(false);
    const [editingId, setEditingId] = React.useState<string | null>(null);
    const [formError, setFormError] = React.useState<string | null>(null);
    const [isEditUploading, setIsEditUploading] = React.useState(false);

    // Debounce the term that actually drives the request so we don't fire a
    // query per keystroke.
    React.useEffect(() => {
        const handle = setTimeout(
            () => setDebouncedSearch(search),
            SEARCH_DEBOUNCE_MS,
        );
        return () => clearTimeout(handle);
    }, [search]);

    // A new search starts from page 1.
    React.useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    const query: BranchListQuery = {
        page,
        pageSize,
        search: debouncedSearch.trim() || undefined,
        includeArchived: includeArchived || undefined,
    };
    const branchesQuery = useBranchDetailsQuery(query);

    const response =
        branchesQuery.data && branchesQuery.data.success
            ? branchesQuery.data.response
            : null;

    // Raw items as the server returned them — kept verbatim so the edit handler
    // can seed the form with the *original* relative imageUrl.
    const rawItems: BranchDetailsItem[] | null = response?.items ?? null;

    const data: Branch[] | null = React.useMemo(() => {
        if (rawItems === null) {
            return null;
        }
        return rawItems.map((item) => ({
            ...item,
            imageUrl: item.imageUrl
                ? (getImageDisplayUrl(item.imageUrl, baseUrl) ?? null)
                : null,
        }));
    }, [baseUrl, rawItems]);

    const error: ErrorResponse | null =
        branchesQuery.data && !branchesQuery.data.success
            ? branchesQuery.data.error
            : null;

    const editingRaw = React.useMemo(() => {
        if (editingId === null || rawItems === null) {
            return null;
        }
        return rawItems.find((item) => item.id === editingId) ?? null;
    }, [editingId, rawItems]);

    const editingInitialValues: BranchFormInitialValues | null =
        React.useMemo(() => {
            if (editingRaw === null) {
                return null;
            }
            return {
                name: editingRaw.name,
                address: editingRaw.address,
                area: editingRaw.area === null ? '' : String(editingRaw.area),
                isMain: editingRaw.isMain,
                image: editingRaw.imageUrl
                    ? {
                          imageName: editingRaw.name,
                          imageUrl:
                              getImageDisplayUrl(
                                  editingRaw.imageUrl,
                                  baseUrl,
                              ) ?? editingRaw.imageUrl,
                      }
                    : null,
            };
        }, [baseUrl, editingRaw]);

    const { create, isCreating } = useCreateBranch();
    const updateMutation = useUpdateBranchMutation();
    const deleteMutation = useDeleteBranchMutation();

    const onSearchChange = React.useCallback((value: string) => {
        setSearch(value);
    }, []);

    const onPageChange = React.useCallback((next: number) => {
        setPage(next);
    }, []);

    const onPageSizeChange = React.useCallback((next: number) => {
        setPageSize(next);
        setPage(1);
    }, []);

    const onAddBranch = React.useCallback(() => {
        setEditingId(null);
        setFormError(null);
        setIsFormOpen(true);
    }, []);

    const onEditBranch = React.useCallback((id: string) => {
        setEditingId(id);
        setFormError(null);
        setIsFormOpen(true);
    }, []);

    const onCloseForm = React.useCallback(() => {
        setIsFormOpen(false);
        setEditingId(null);
        setFormError(null);
    }, []);

    // Read once per mount, like `useBranchesPage`: the session cannot change
    // without a navigation that remounts this page.
    const user = React.useMemo(() => getUser(), []);
    const canImport = React.useCallback(
        (branch: Branch) => canImportProducts(user, branch),
        [user],
    );

    const navigate = useNavigate();
    const onImportProducts = React.useCallback(
        (id: string) => navigate(PATH.BRANCHES.BIND_IMPORT(id)),
        [navigate],
    );

    const archiveMutation = useSetBranchArchivedMutation();

    // A branch delete can be refused for four different reasons, each with its
    // own remedy (bos-api BOS-121). The dialog is where that gets read.
    const deleteConfirm = useDeleteConfirm({
        onDelete: (id) => deleteMutation.mutateAsync(id),
        onArchive: (id) => archiveMutation.mutateAsync({ id, archived: true }),
    });

    const onDeleteBranch = React.useCallback(
        (id: string) => {
            const branch = data?.find((row) => row.id === id);
            deleteConfirm.onRequestDelete({ id, name: branch?.name ?? id });
        },
        [data, deleteConfirm],
    );

    const archiveConfirm = useArchiveBranchConfirm({
        onArchive: (id) => archiveMutation.mutateAsync({ id, archived: true }),
    });

    const onRequestArchive = React.useCallback(
        (id: string) => {
            const branch = data?.find((row) => row.id === id);
            archiveConfirm.onRequestArchive({ id, name: branch?.name ?? id });
        },
        [archiveConfirm, data],
    );

    const onSetArchived = React.useCallback(
        (id: string, archived: boolean) => {
            archiveMutation.mutate({ id, archived });
        },
        [archiveMutation],
    );

    const onToggleArchived = React.useCallback((next: boolean) => {
        setIncludeArchived(next);
        setPage(1);
    }, []);

    const onRetry = React.useCallback(() => {
        branchesQuery.refetch();
    }, [branchesQuery]);

    const onSubmitBranch = React.useCallback(
        async (input: BranchFormSubmit) => {
            setFormError(null);

            if (editingId === null) {
                // ---- Create flow (single multipart round-trip) ------------
                // Shared with the Control Center's "Add branch", which opens
                // this same form from the launcher — see `useCreateBranch`.
                const created = await create(input);
                if (!created.ok) {
                    setFormError(created.message);
                    return;
                }
                setIsFormOpen(false);
                return;
            }

            // ---- Edit flow ----------------------------------------------
            let nextImageUrl: string | null = input.imageUrl;
            if (input.imageUpload) {
                setIsEditUploading(true);
                try {
                    const uploadResult = await apiClient.images.uploadImage(
                        input.imageUpload,
                        'branches',
                    );
                    if (!uploadResult.success) {
                        setFormError(
                            uploadResult.error.message ??
                                'Failed to upload image.',
                        );
                        return;
                    }
                    nextImageUrl = uploadResult.response.imageUrl;
                } finally {
                    setIsEditUploading(false);
                }
            }

            const updateResult = await updateMutation.mutateAsync({
                id: editingId,
                body: {
                    name: input.name,
                    address: input.address,
                    isMain: input.isMain,
                    area: input.area,
                    imageUrl: nextImageUrl,
                },
            });
            if (!updateResult.success) {
                setFormError(
                    updateResult.error.message ?? 'Failed to update branch.',
                );
                return;
            }
            setEditingId(null);
            setIsFormOpen(false);
        },
        [apiClient, create, editingId, updateMutation],
    );

    return {
        archiveConfirm,
        canImportProducts: canImport,
        deleteConfirm,
        includeArchived,
        onRequestArchive,
        onSetArchived,
        onToggleArchived,
        data,
        editingInitialValues,
        error,
        formError,
        formMode: editingId === null ? 'create' : 'edit',
        isFormOpen,
        isLoading: branchesQuery.isLoading,
        isSubmitting: isCreating || updateMutation.isPending || isEditUploading,
        onAddBranch,
        onCloseForm,
        onDeleteBranch,
        onEditBranch,
        onImportProducts,
        onPageChange,
        onPageSizeChange,
        onRetry,
        onSearchChange,
        onSubmitBranch,
        page,
        pageSize,
        search,
        totalCount: response?.totalCount ?? 0,
        totalPages: response?.totalPages ?? 1,
    };
};
